import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  MessageCircle, 
  Bell, 
  CheckCircle, 
  XCircle, 
  Clock, 
  RefreshCw, 
  Send, 
  Calendar,
  Phone,
  User,
  Activity,
  AlertCircle,
  Eye,
  PlayCircle,
  BarChart3
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WhatsAppRemindersPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [error, setError] = useState(null);
  const [triggerResult, setTriggerResult] = useState(null);
  const [upcoming, setUpcoming] = useState(null);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [sendingId, setSendingId] = useState(null);
  const [sendingNoshowId, setSendingNoshowId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch stats
      const statsParams = selectedDate ? `?date=${selectedDate}` : '';
      const statsRes = await axios.get(`${API_URL}/api/whatsapp/reminders/stats${statsParams}`, { headers });
      setStats(statsRes.data);

      // Fetch reminders
      let remindersParams = [];
      if (selectedDate) remindersParams.push(`date=${selectedDate}`);
      if (statusFilter) remindersParams.push(`status=${statusFilter}`);
      const remindersQuery = remindersParams.length > 0 ? `?${remindersParams.join('&')}` : '';
      const remindersRes = await axios.get(`${API_URL}/api/whatsapp/reminders${remindersQuery}`, { headers });
      setReminders(remindersRes.data.reminders || []);

      // Fetch upcoming messages
      const upcomingRes = await axios.get(`${API_URL}/api/whatsapp/upcoming`, { headers });
      setUpcoming(upcomingRes.data);

    } catch (err) {
      console.error('Error fetching WhatsApp data:', err);
      setError(err.response?.data?.detail || 'Failed to fetch data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedDate, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleTriggerReminders = async () => {
    setTriggering(true);
    setTriggerResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/admin/trigger-whatsapp-reminders`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTriggerResult(res.data.result);
      // Refresh data after triggering
      setTimeout(() => fetchData(), 1000);
    } catch (err) {
      console.error('Error triggering reminders:', err);
      setError(err.response?.data?.detail || 'Failed to trigger reminders');
    } finally {
      setTriggering(false);
    }
  };

  const handleSendIndividual = async (appointmentId, patientName) => {
    setSendingId(appointmentId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/whatsapp/send-individual/${appointmentId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setSendingId(null);
    }
  };

  const handleSendNoshowIndividual = async (msg, index) => {
    setSendingNoshowId(index);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/whatsapp/send-noshow-individual`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            patient_phone: msg.patient_phone,
            patient_name: msg.patient_name,
            scheduled_date: msg.scheduled_date,
            service_name: msg.service_name
          }
        }
      );
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send no-show reminder');
    } finally {
      setSendingNoshowId(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-gray-100 text-gray-800', icon: Clock },
      sent: { color: 'bg-blue-100 text-blue-800', icon: Send },
      delivered: { color: 'bg-indigo-100 text-indigo-800', icon: CheckCircle },
      read: { color: 'bg-purple-100 text-purple-800', icon: Eye },
      confirmed: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      rescheduled: { color: 'bg-yellow-100 text-yellow-800', icon: Calendar },
      cancelled: { color: 'bg-red-100 text-red-800', icon: XCircle },
      failed: { color: 'bg-red-100 text-red-800', icon: AlertCircle }
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {status?.toUpperCase()}
      </Badge>
    );
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      let d = dateStr;
      // Old records have no timezone offset - treat as IST
      if (typeof d === 'string' && !d.includes('+') && !d.includes('Z')) {
        d = d + '+05:30';
      }
      return new Date(d).toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-green-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-xl">
              <MessageCircle className="w-8 h-8 text-green-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">WhatsApp Reminders</h1>
              <p className="text-sm text-gray-500">Monitor 24-hour appointment reminders</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              onClick={handleRefresh}
              disabled={refreshing}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            {user?.role === 'Admin' && (
              <Button 
                onClick={handleTriggerReminders}
                disabled={triggering}
                className="bg-green-600 hover:bg-green-700"
                data-testid="trigger-reminders-btn"
              >
                <PlayCircle className={`w-4 h-4 mr-2 ${triggering ? 'animate-pulse' : ''}`} />
                {triggering ? 'Sending...' : 'Send Reminders Now'}
              </Button>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            <AlertCircle className="w-5 h-5 inline mr-2" />
            {error}
          </div>
        )}

        {/* Trigger Result */}
        {triggerResult && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <h3 className="font-semibold text-green-900 mb-2">Reminder Job Completed</h3>
            <div className="flex gap-6 text-sm">
              <span className="text-green-700">
                <CheckCircle className="w-4 h-4 inline mr-1" />
                Sent: {triggerResult.sent}
              </span>
              <span className="text-red-700">
                <XCircle className="w-4 h-4 inline mr-1" />
                Failed: {triggerResult.failed}
              </span>
              <span className="text-gray-600">
                <Clock className="w-4 h-4 inline mr-1" />
                Skipped: {triggerResult.skipped}
              </span>
              <span className="text-gray-600">
                <Activity className="w-4 h-4 inline mr-1" />
                Total Appointments: {triggerResult.total_appointments}
              </span>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-6">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-blue-600 font-medium">Total Sent</p>
                    <p className="text-2xl font-bold text-blue-900">{stats.sent + stats.delivered + stats.read}</p>
                  </div>
                  <Send className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-green-600 font-medium">Confirmed</p>
                    <p className="text-2xl font-bold text-green-900">{stats.confirmed}</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-yellow-600 font-medium">Rescheduled</p>
                    <p className="text-2xl font-bold text-yellow-900">{stats.rescheduled}</p>
                  </div>
                  <Calendar className="w-8 h-8 text-yellow-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-red-600 font-medium">Cancelled</p>
                    <p className="text-2xl font-bold text-red-900">{stats.cancelled}</p>
                  </div>
                  <XCircle className="w-8 h-8 text-red-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-purple-600 font-medium">Response Rate</p>
                    <p className="text-2xl font-bold text-purple-900">{stats.response_rate}%</p>
                  </div>
                  <BarChart3 className="w-8 h-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-4">
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition ${
              activeTab === 'upcoming' ? 'border-green-600 text-green-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid="tab-upcoming"
          >
            <Clock className="w-4 h-4 inline mr-1.5" />
            Upcoming ({(upcoming?.total_upcoming || 0) + (upcoming?.total_noshow || 0)})
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition ${
              activeTab === 'history' ? 'border-green-600 text-green-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid="tab-history"
          >
            <Bell className="w-4 h-4 inline mr-1.5" />
            Sent History ({reminders.length})
          </button>
        </div>

        {/* Upcoming Messages Tab */}
        {activeTab === 'upcoming' && upcoming && (
          <div className="space-y-6">
            {/* Upcoming Appointment Reminders */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Send className="w-5 h-5 text-blue-600" />
                  Upcoming Appointment Reminders (Next 48 hrs)
                  <Badge variant="outline" className="ml-2">{upcoming.total_upcoming}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {upcoming.upcoming_reminders?.length === 0 ? (
                  <p className="text-center text-gray-400 py-6">No upcoming appointment reminders</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 uppercase">Patient</th>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 uppercase">Service</th>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 uppercase">Date/Time</th>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 uppercase">Template</th>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 uppercase">Status</th>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 uppercase">Message Preview</th>
                          <th className="text-center py-2 px-3 text-xs font-semibold text-gray-600 uppercase">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {upcoming.upcoming_reminders.map((msg, i) => (
                          <tr key={`up-${i}`} className="border-b border-gray-50 hover:bg-blue-50/30">
                            <td className="py-2.5 px-3">
                              <p className="font-medium text-gray-900 text-sm">{msg.patient_name}</p>
                              <p className="text-xs text-gray-500">{msg.patient_phone}</p>
                            </td>
                            <td className="py-2.5 px-3 text-sm text-gray-700">{msg.service_name}</td>
                            <td className="py-2.5 px-3 text-sm text-gray-700">
                              {msg.scheduled_date}
                              {msg.scheduled_time && msg.scheduled_time !== 'queue' && (
                                <span className="text-xs text-gray-400 ml-1">at {msg.scheduled_time}</span>
                              )}
                            </td>
                            <td className="py-2.5 px-3">
                              <Badge variant="outline" className="text-[10px]">{msg.template}</Badge>
                            </td>
                            <td className="py-2.5 px-3">
                              {msg.status === 'already_sent' ? (
                                <Badge className="bg-green-100 text-green-800 text-xs"><CheckCircle className="w-3 h-3 mr-1" />Sent</Badge>
                              ) : (
                                <Badge className="bg-amber-100 text-amber-800 text-xs"><Clock className="w-3 h-3 mr-1" />Scheduled</Badge>
                              )}
                            </td>
                            <td className="py-2.5 px-3 text-xs text-gray-500 max-w-[300px] truncate">{msg.message_preview}</td>
                            <td className="py-2.5 px-3 text-center">
                              {msg.status === 'already_sent' ? (
                                <span className="text-xs text-gray-400">Sent</span>
                              ) : (
                                <Button
                                  size="sm"
                                  className="h-7 bg-green-600 hover:bg-green-700 text-xs"
                                  disabled={sendingId === msg.appointment_id}
                                  onClick={() => handleSendIndividual(msg.appointment_id, msg.patient_name)}
                                  data-testid={`send-individual-${msg.appointment_id}`}
                                >
                                  <Send className="w-3 h-3 mr-1" />
                                  {sendingId === msg.appointment_id ? 'Sending...' : 'Send Now'}
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* No-Show Follow-ups */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <AlertCircle className="w-5 h-5 text-orange-600" />
                  No-Show Follow-up Messages
                  <Badge variant="outline" className="ml-2">{upcoming.total_noshow}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {upcoming.noshow_followups?.length === 0 ? (
                  <p className="text-center text-gray-400 py-6">No pending no-show follow-ups</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 uppercase">Patient</th>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 uppercase">Service</th>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 uppercase">No-Show Date</th>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 uppercase">Sequence Step</th>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 uppercase">Status</th>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 uppercase">Message Preview</th>
                          <th className="text-center py-2 px-3 text-xs font-semibold text-gray-600 uppercase">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {upcoming.noshow_followups.map((msg, i) => (
                          <tr key={`ns-${i}`} className="border-b border-gray-50 hover:bg-orange-50/30">
                            <td className="py-2.5 px-3">
                              <p className="font-medium text-gray-900 text-sm">{msg.patient_name}</p>
                              <p className="text-xs text-gray-500">{msg.patient_phone}</p>
                            </td>
                            <td className="py-2.5 px-3 text-sm text-gray-700">{msg.service_name}</td>
                            <td className="py-2.5 px-3 text-sm text-gray-700">{msg.scheduled_date}</td>
                            <td className="py-2.5 px-3 text-sm font-medium text-gray-700">{msg.trigger_time}</td>
                            <td className="py-2.5 px-3">
                              {msg.status === 'due_now' ? (
                                <Badge className="bg-red-100 text-red-800 text-xs"><AlertCircle className="w-3 h-3 mr-1" />Due Now</Badge>
                              ) : (
                                <Badge className="bg-gray-100 text-gray-700 text-xs"><Clock className="w-3 h-3 mr-1" />Scheduled</Badge>
                              )}
                            </td>
                            <td className="py-2.5 px-3 text-xs text-gray-500 max-w-[300px] truncate">{msg.message_preview}</td>
                            <td className="py-2.5 px-3 text-center">
                              <Button
                                size="sm"
                                className="h-7 bg-orange-600 hover:bg-orange-700 text-xs"
                                disabled={sendingNoshowId === i}
                                onClick={() => handleSendNoshowIndividual(msg, i)}
                                data-testid={`send-noshow-${i}`}
                              >
                                <Send className="w-3 h-3 mr-1" />
                                {sendingNoshowId === i ? 'Sending...' : 'Send Now'}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (<>
        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Date</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              data-testid="date-filter"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 min-w-[150px]"
              data-testid="status-filter"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="sent">Sent</option>
              <option value="delivered">Delivered</option>
              <option value="read">Read</option>
              <option value="confirmed">Confirmed</option>
              <option value="rescheduled">Rescheduled</option>
              <option value="cancelled">Cancelled</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          {(selectedDate || statusFilter) && (
            <div className="flex items-end">
              <Button
                variant="ghost"
                onClick={() => {
                  setSelectedDate('');
                  setStatusFilter('');
                }}
                className="text-gray-500"
              >
                Clear Filters
              </Button>
            </div>
          )}
        </div>

        {/* Reminders Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-green-600" />
              Reminder History
              <Badge variant="outline" className="ml-2">{reminders.length} records</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {reminders.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <MessageCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium">No reminders found</p>
                <p className="text-sm">
                  {selectedDate || statusFilter 
                    ? 'Try adjusting your filters'
                    : 'Reminders will appear here when sent'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Patient</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Type</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Service</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Date</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Status</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Response</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Sent At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reminders.map((reminder) => (
                      <tr key={reminder.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-gray-400" />
                            <div>
                              <p className="font-medium text-gray-900">{reminder.patient_name}</p>
                              <p className="text-xs text-gray-500 flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {reminder.patient_phone}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {reminder.msg_type === 'noshow_followup' ? (
                            <Badge className="bg-orange-100 text-orange-800 text-xs">No-Show</Badge>
                          ) : (
                            <Badge className="bg-blue-100 text-blue-800 text-xs">Reminder</Badge>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-700">{reminder.service_name}</td>
                        <td className="py-3 px-4">
                          {reminder.appointment_id ? (
                          <div
                            className="text-sm cursor-pointer hover:bg-blue-50 rounded p-1 -m-1 transition"
                            onClick={() => navigate(`/?date=${reminder.scheduled_date}&highlight=${reminder.appointment_id}`)}
                            title="Click to view appointment in Dashboard"
                            data-testid={`appointment-link-${reminder.id}`}
                          >
                            <p className="font-medium text-blue-600 hover:text-blue-800 underline underline-offset-2">{reminder.scheduled_date}</p>
                            <p className="text-xs text-gray-500">{reminder.scheduled_time || 'No time'}</p>
                            {reminder.appointment_ref_number && (
                              <p className="text-xs text-blue-600">Ref: {reminder.appointment_ref_number}</p>
                            )}
                          </div>
                          ) : (
                            <p className="text-sm text-gray-700">{reminder.scheduled_date}</p>
                          )}
                        </td>
                        <td className="py-3 px-4">{getStatusBadge(reminder.status)}</td>
                        <td className="py-3 px-4">
                          {reminder.patient_response ? (
                            <div>
                              <p className="text-sm font-medium text-gray-900">{reminder.patient_response}</p>
                              <p className="text-xs text-gray-500">
                                {formatDateTime(reminder.response_received_at)}
                              </p>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {formatDateTime(reminder.sent_at)}
                          {reminder.failed_reason && (
                            <p className="text-xs text-red-500 mt-1">{reminder.failed_reason}</p>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Section */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-semibold text-blue-900 mb-2">How It Works</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• <strong>Automatic:</strong> Reminders are sent daily at 9:00 AM IST for appointments in the next 24 hours</li>
            <li>• <strong>Manual Trigger:</strong> Admins can manually trigger reminders using the &quot;Send Reminders Now&quot; button</li>
            <li>• <strong>No-Show Sequence:</strong> Day 0 (immediate), Day 7, Day 21, Day 45 follow-ups for missed appointments</li>
            <li>• <strong>Patient Response:</strong> Patients can reply with &quot;Yes/Confirm&quot;, &quot;No/Cancel&quot;, or &quot;Reschedule&quot;</li>
            <li>• <strong>Call to Reschedule:</strong> +917710117777</li>
          </ul>
        </div>
        </>)}
      </div>
    </Layout>
  );
};

export default WhatsAppRemindersPage;
